/**
 * Parses the architectural distance a user types during scale calibration: `3'-0"`, `3'`,
 * `36"`, `10'-6"`, or a bare number meaning inches.
 *
 * This is a deliberate port of ArchitecturalDistance.TryParseInches in the recognition
 * engine. The browser and the engine have to agree on what a typed distance means, because
 * the number this produces becomes inchesPerPreviewPixel, which scales every wall in the
 * drawing. A disagreement here is not a parse bug, it is a wrong-sized building.
 *
 * One deliberate divergence, documented in the tests: the C# implementation reaches
 * double.TryParse, which accepts "Infinity" and would return an infinite measurement. This
 * rejects any non-finite result instead.
 */

/**
 * Mirrors NumberStyles.Float with CultureInfo.InvariantCulture: optional sign, decimal
 * point, exponent, surrounding whitespace. Notably not JavaScript's Number(), which also
 * accepts hex, Infinity, and the empty string.
 */
const INVARIANT_FLOAT = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/** Feet-and-inches forms. Both parts optional, matching the engine's regex exactly. */
const FEET_INCHES = /^\s*(?:([+-]?\d+(?:\.\d+)?)\s*')?\s*-?\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in)?)?\s*$/i;

function parseInvariantFloat(text: string): number | null {
  const trimmed = text.trim();
  if (!INVARIANT_FLOAT.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Returns the distance in inches, or null when the text is not a distance the engine would
 * accept. A non-positive result is rejected rather than returned: the engine treats
 * `inches > 0` as part of parsing succeeding, so "0" and "-3'" are not distances.
 */
export function parseArchitecturalDistanceInches(text: string | null | undefined): number | null {
  if (text === null || text === undefined || text.trim() === "") return null;

  const value = text.trim().toLowerCase();

  // A bare number is inches. The engine tries this first and returns immediately on a
  // successful parse, so "0" fails outright rather than falling through to the regex.
  const plain = parseInvariantFloat(value);
  if (plain !== null) return plain > 0 ? plain : null;

  const match = FEET_INCHES.exec(value);
  if (match === null) return null;

  const feetText = match[1];
  const inchesText = match[2];

  let feet = 0;
  if (feetText !== undefined) {
    const parsed = parseInvariantFloat(feetText);
    if (parsed === null) return null;
    feet = parsed;
  }

  let inches = 0;
  if (inchesText !== undefined) {
    const parsed = parseInvariantFloat(inchesText);
    if (parsed === null) return null;
    inches = parsed;
  }

  const total = feet * 12 + inches;
  return total > 0 ? total : null;
}
